import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import {
  addDays,
  addMonths,
  endOfMonth,
  isPast,
  isToday,
  isWithinInterval,
  max,
  startOfMonth,
  startOfToday,
} from "date-fns";
import { Loader2 } from "lucide-react";
import { navigate, useQueryParams } from "raviger";
import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { toast } from "sonner";

import { cn } from "@/lib/utils";

import Calendar from "@/CAREUI/interactive/Calendar";

import { Button } from "@/components/ui/button";
import { Card } from "@/components/ui/card";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";

import { Avatar } from "@/components/Common/Avatar";
import Loading from "@/components/Common/Loading";

import { usePatientContext } from "@/hooks/usePatientUser";

import mutate from "@/Utils/request/mutate";
import query from "@/Utils/request/query";
import { dateQueryString, formatName } from "@/Utils/utils";
import AvailabilityDateButton from "@/pages/Appointments/BookAppointment/AvailabilityDateButton";
import { TokenSlotButton } from "@/pages/Appointments/BookAppointment/AppointmentSlotPicker";
import {
  groupSlotsByAvailability,
  summarizeSlotAvailability,
} from "@/pages/Appointments/utils";
import {
  getPublicPendingBookingStorageKey,
  savePublicPendingBooking,
} from "@/pages/PublicAppointments/pendingBookingStorage";
import { getBasePrice } from "@/types/base/monetaryComponent/monetaryComponent";
import publicFacilityApi from "@/types/facility/publicFacilityApi";
import PublicAppointmentApi from "@/types/scheduling/PublicAppointmentApi";
import {
  SchedulableResourceType,
  TokenSlot,
} from "@/types/scheduling/schedule";
import scheduleApis from "@/types/scheduling/scheduleApi";

interface AppointmentsProps {
  facilityId: string;
  staffId: string;
  appointmentId?: string;
}

function parseSelectedDate(value: unknown) {
  if (typeof value !== "string" || !value) {
    return new Date();
  }

  const parsedDate = new Date(`${value}T00:00:00`);
  return Number.isNaN(parsedDate.getTime()) ? new Date() : parsedDate;
}

export function ScheduleAppointment(props: AppointmentsProps) {
  const { t } = useTranslation();
  const { facilityId, staffId, appointmentId } = props;
  const [
    { date: dateQuery, slotId: selectedSlotIdQuery, reason: reasonQuery },
  ] = useQueryParams();
  const initialSelectedDate = parseSelectedDate(dateQuery);
  const [selectedMonth, setSelectedMonth] = useState(initialSelectedDate);
  const [selectedDate, setSelectedDate] = useState(initialSelectedDate);
  const [selectedSlot, setSelectedSlot] = useState<TokenSlot>();
  const [reason, setReason] = useState(
    typeof reasonQuery === "string" ? reasonQuery : "",
  );
  const queryClient = useQueryClient();

  const patientUserContext = usePatientContext();
  const tokenData = patientUserContext?.tokenData;

  if (!staffId) {
    toast.error(t("staff_username_not_found"));
    navigate(`/facility/${facilityId}`);
  } else if (!tokenData) {
    toast.error(t("phone_number_not_found"));
    navigate(`/facility/${facilityId}/appointments/${staffId}/otp/send`);
  }

  const { data: appointmentData } = useQuery({
    queryKey: ["appointment", tokenData?.phoneNumber],
    queryFn: query(PublicAppointmentApi.getAppointments, {
      headers: { Authorization: `Bearer ${tokenData?.token}` },
    }),
    enabled: !!appointmentId && !!tokenData?.token,
  });

  const appointment = appointmentData?.results.find(
    (appointment) => appointment.id === appointmentId,
  );

  useEffect(() => {
    if (appointment) {
      setReason(appointment.note);
    }
  }, [appointment]);

  useEffect(() => {
    const restoredDate = parseSelectedDate(dateQuery);
    setSelectedMonth(restoredDate);
    setSelectedDate(restoredDate);
  }, [dateQuery]);

  useEffect(() => {
    if (!appointment && typeof reasonQuery === "string") {
      setReason(reasonQuery);
    }
  }, [appointment, reasonQuery]);

  const { data: facilityResponse, error: facilityError } = useQuery({
    queryKey: ["facility", facilityId],
    queryFn: query(publicFacilityApi.getAny, {
      pathParams: { id: facilityId },
      silent: true,
    }),
  });

  if (facilityError) {
    toast.error(t("error_fetching_facility_data"));
  }

  const { data: userData, error: userError } = useQuery({
    queryKey: ["user", facilityId, staffId],
    queryFn: query(
      scheduleApis.appointments.getPublicScheduleableFacilityUser,
      {
        pathParams: { facility_id: facilityId, user_id: staffId },
      },
    ),
    enabled: !!facilityId && !!staffId,
  });

  if (userError) {
    toast.error(t("error_fetching_user_data"));
  }

  const filterDisplayableSlot = (slot: TokenSlot) => {
    const isCurrentlyActive = isWithinInterval(new Date(), {
      start: slot.start_datetime,
      end: slot.end_datetime,
    });

    const isCurrentAppointmentSlot =
      appointment && slot.id === appointment.token_slot.id;

    return !isCurrentlyActive && !isCurrentAppointmentSlot;
  };

  const getDisplayableSlots = (slots: TokenSlot[]) => {
    return slots.filter(filterDisplayableSlot);
  };

  const buildAvailabilityStatsQueryOptions = (month: Date) => {
    const fromDate = max([startOfMonth(month), startOfToday()]);
    const toDate = max([fromDate, endOfMonth(month)]);

    return {
      queryKey: [
        "public-availability-heatmap",
        facilityId,
        staffId,
        dateQueryString(fromDate),
        dateQueryString(toDate),
      ],
      queryFn: query(PublicAppointmentApi.availabilityStats, {
        body: {
          facility: facilityId,
          resource_type: SchedulableResourceType.Practitioner,
          resource_id: staffId,
          from_date: dateQueryString(fromDate),
          to_date: dateQueryString(toDate),
        },
        headers: {
          Authorization: `Bearer ${tokenData.token}`,
        },
        silent: true,
      }),
    };
  };

  const buildSlotsForDayQueryOptions = (date: Date) => {
    return {
      queryKey: ["public-slots", facilityId, staffId, dateQueryString(date)],
      queryFn: query(PublicAppointmentApi.getSlotsForDay, {
        body: {
          facility: facilityId,
          resource_type: SchedulableResourceType.Practitioner,
          resource_id: staffId,
          day: dateQueryString(date),
        },
        headers: {
          Authorization: `Bearer ${tokenData.token}`,
        },
        silent: true,
      }),
    };
  };

  const availabilityStatsQuery = useQuery({
    ...buildAvailabilityStatsQueryOptions(selectedMonth),
    enabled: !!staffId && !!tokenData?.token,
  });

  const todaySlotsQuery = useQuery({
    ...buildSlotsForDayQueryOptions(new Date()),
    select: (data: { results: TokenSlot[] }) => {
      return getDisplayableSlots(data.results);
    },
    enabled: !!staffId && !!tokenData?.token,
  });

  const slotsQuery = useQuery({
    ...buildSlotsForDayQueryOptions(selectedDate),
    select: (data: { results: TokenSlot[] }) => {
      return getDisplayableSlots(data.results);
    },
    refetchInterval: 10000,
    enabled: !!selectedDate && !!tokenData.token,
  });

  if (slotsQuery.error) {
    if (
      slotsQuery.error.cause?.errors &&
      Array.isArray(slotsQuery.error.cause.errors) &&
      slotsQuery.error.cause.errors[0][0] === "Resource is not schedulable"
    ) {
      toast.error(t("user_not_available_for_appointments"));
    } else {
      toast.error(t("error_fetching_slots_data"));
    }
  }

  const selectedChargeDefinition =
    selectedSlot?.availability.charge_item_definition ?? null;
  const selectedChargeAmount = selectedChargeDefinition
    ? getBasePrice(selectedChargeDefinition.price_components).toString()
    : null;

  const { mutateAsync: reserveAppointment, isPending: isReservingAppointment } =
    useMutation({
      mutationFn: mutate(PublicAppointmentApi.reserveAppointment, {
        pathParams: { id: selectedSlot?.id || "" },
        headers: {
          Authorization: `Bearer ${tokenData.token}`,
        },
      }),
    });
  const {
    mutateAsync: rescheduleAppointment,
    isPending: isReschedulingAppointment,
  } = useMutation({
    mutationFn: mutate(PublicAppointmentApi.rescheduleAppointment, {
      headers: {
        Authorization: `Bearer ${tokenData.token}`,
      },
    }),
  });

  const handleContinue = async () => {
    if (!selectedSlot) return;

    if (appointmentId && !appointment) {
      return;
    }

    if (appointmentId && appointment) {
      try {
        const rescheduledAppointment = await rescheduleAppointment({
          appointment: appointmentId,
          new_slot: selectedSlot.id,
          new_booking_note: reason,
          previous_booking_note: appointment.note,
        });

        await queryClient.invalidateQueries({
          queryKey: ["appointment", tokenData.phoneNumber],
        });
        await queryClient.invalidateQueries({
          queryKey: ["public-slots", facilityId, staffId],
        });
        await queryClient.invalidateQueries({
          queryKey: ["public-availability-heatmap", facilityId, staffId],
        });

        toast.success(t("appointment_rescheduled"));
        navigate(
          `/facility/${facilityId}/appointments/${rescheduledAppointment.id}/success`,
          { replace: true },
        );
      } catch {
        toast.error(
          t("failed_to_reschedule_slot", {
            defaultValue: "Unable to reschedule this appointment right now.",
          }),
        );
      }

      return;
    }

    const patientSelectPath = `/facility/${facilityId}/appointments/${staffId}/patient-select`;
    const patientSelectQuery = {
      slotId: selectedSlot.id,
      date: dateQueryString(selectedDate),
      reason,
      ...(selectedChargeDefinition
        ? {
            consultationTitle: selectedChargeDefinition.title,
            consultationAmount: selectedChargeAmount ?? "",
          }
        : {}),
    };

    if (!selectedChargeDefinition) {
      navigate(patientSelectPath, { query: patientSelectQuery });
      return;
    }

    try {
      const reservedAppointment = await reserveAppointment({ note: reason });
      savePublicPendingBooking(
        getPublicPendingBookingStorageKey(
          facilityId,
          staffId,
          selectedSlot.id,
          tokenData.phoneNumber,
        ),
        {
          appointmentId: reservedAppointment.id,
          patientId: null,
          pendingExpiry: reservedAppointment.pending_expiry,
        },
      );
      await queryClient.invalidateQueries({
        queryKey: ["appointment", tokenData.phoneNumber],
      });
      await queryClient.invalidateQueries({
        queryKey: ["public-slots", facilityId, staffId],
      });
      await queryClient.invalidateQueries({
        queryKey: ["public-availability-heatmap", facilityId, staffId],
      });
      navigate(patientSelectPath, { query: patientSelectQuery });
    } catch {
      toast.error(
        t("failed_to_reserve_slot", {
          defaultValue: "Unable to reserve this slot right now.",
        }),
      );
    }
  };

  useEffect(() => {
    setSelectedSlot(undefined);
  }, [selectedDate]);

  useEffect(() => {
    const targetSlotId =
      selectedSlot?.id ||
      (typeof selectedSlotIdQuery === "string"
        ? selectedSlotIdQuery
        : undefined);

    if (!targetSlotId || !slotsQuery.data?.length) {
      return;
    }

    for (const { availability, slots } of groupSlotsByAvailability(
      slotsQuery.data,
    )) {
      const matchingSlot = slots.find((slot) => slot.id === targetSlotId);
      if (!matchingSlot) {
        continue;
      }

      if (
        selectedSlot?.id !== matchingSlot.id ||
        selectedSlot.availability?.name !== availability.name
      ) {
        setSelectedSlot({ ...matchingSlot, availability });
      }

      return;
    }
  }, [
    selectedSlot?.availability?.name,
    selectedSlot?.id,
    selectedSlotIdQuery,
    slotsQuery.data,
  ]);

  const getAvailabilityForDate = (date: Date) => {
    if (isToday(date) && todaySlotsQuery.data) {
      const slots = todaySlotsQuery.data.filter(
        (slot) => !isPast(slot.end_datetime),
      );
      return summarizeSlotAvailability(slots);
    }

    return availabilityStatsQuery.data?.[dateQueryString(date)];
  };

  const findNextAvailableDate = async (startDate: Date) => {
    const searchStart = max([startDate, startOfToday()]);

    for (let monthOffset = 0; monthOffset < 6; monthOffset++) {
      const month = addMonths(searchStart, monthOffset);
      const availabilityStats = await queryClient.fetchQuery(
        buildAvailabilityStatsQueryOptions(month),
      );
      const minDate =
        monthOffset === 0
          ? searchStart
          : max([startOfMonth(month), startOfToday()]);
      const nextDateKey = Object.keys(availabilityStats)
        .sort()
        .find(
          (dayKey) =>
            dayKey >= dateQueryString(minDate) &&
            availabilityStats[dayKey].available_time_slots > 0,
        );

      if (nextDateKey) {
        return parseSelectedDate(nextDateKey);
      }
    }

    return null;
  };

  const handleTodayClick = async (today: Date) => {
    setSelectedMonth(today);

    const todaySlots = await queryClient.fetchQuery(
      buildSlotsForDayQueryOptions(today),
    );
    const todayAvailability = summarizeSlotAvailability(
      getDisplayableSlots(todaySlots.results).filter(
        (slot) => !isPast(slot.end_datetime),
      ),
    );

    if (todayAvailability.available_time_slots > 0) {
      setSelectedDate(today);
      return;
    }

    const nextAvailableDate = await findNextAvailableDate(addDays(today, 1));
    if (nextAvailableDate) {
      toast.info(
        t("today_not_available_moving_to_next_available_date", {
          defaultValue:
            "Today is not available. Moving you to the next available date.",
        }),
      );
      setSelectedMonth(nextAvailableDate);
      setSelectedDate(nextAvailableDate);
      return;
    }

    toast.info(
      t("today_not_available_no_upcoming_dates", {
        defaultValue:
          "Today is not available and there are no upcoming available dates right now.",
      }),
    );
    setSelectedDate(today);
  };
  const renderDay = (date: Date) => {
    return (
      <AvailabilityDateButton
        availability={getAvailabilityForDate(date)}
        date={date}
        isLoading={
          availabilityStatsQuery.isFetching && !getAvailabilityForDate(date)
        }
        onSelectDate={setSelectedDate}
        selectedDate={selectedDate}
      />
    );
  };

  if (!userData) {
    return <Loading />;
  }

  return (
    <div className="flex flex-col">
      <div className="container mx-auto px-4 py-8">
        <div className="flex px-2 pb-4 justify-start">
          <Button
            variant="outline"
            className="border border-secondary-400"
            onClick={() => navigate(`/facility/${facilityId}`)}
          >
            <span className="text-sm">{t("back")}</span>
          </Button>
        </div>
        <div className="flex flex-col sm:flex-row gap-4">
          <div className="sm:w-1/3">
            <Card className={cn("overflow-hidden bg-white")}>
              <div className="flex flex-col">
                <div className="flex flex-col gap-4 py-4 justify-between h-full">
                  <Avatar
                    imageUrl={userData.profile_picture_url}
                    name={formatName(userData, true)}
                    className="size-96 self-center rounded-sm"
                  />

                  <div className="flex grow flex-col px-4">
                    <h3 className="truncate text-xl font-semibold">
                      {formatName(userData)}
                    </h3>
                    <p className="text-sm text-gray-500 truncate">
                      {userData.user_type}
                    </p>

                    {/* <p className="text-xs mt-4">Education: </p>
                    <p className="text-sm text-gray-500 truncate">
                      {userData.qualification}
                    </p> */}
                  </div>
                </div>

                <div className="mt-auto border-t border-gray-100 bg-gray-50 p-4">
                  <div className="flex justify-between items-center">
                    <div className="text-sm text-gray-500">
                      {facilityResponse?.name}
                    </div>
                  </div>
                </div>
              </div>
            </Card>
          </div>
          <div className="flex-1 mx-2">
            <div className="flex flex-col gap-6">
              <span className="text-base font-semibold">
                {appointmentId
                  ? t("reschedule_appointment_with")
                  : t("book_an_appointment_with")}{" "}
                {formatName(userData)}
              </span>
              <div>
                <Label className="mb-2">{t("note")}</Label>
                <Textarea
                  placeholder={t("appointment_note")}
                  value={reason}
                  onChange={(e) => setReason(e.target.value)}
                />
              </div>
              <Calendar
                month={selectedMonth}
                onTodayClick={(today) => {
                  void handleTodayClick(today);
                  return true;
                }}
                onMonthChange={setSelectedMonth}
                setSelectedDate={setSelectedDate}
                renderDay={renderDay}
                highlightToday={false}
              />
              <div className="space-y-6">
                {slotsQuery.data && slotsQuery.data.length > 0 ? (
                  groupSlotsByAvailability(slotsQuery.data).map(
                    ({ availability, slots }) => (
                      <div key={availability.name}>
                        <h4 className="text-lg font-semibold mb-3">
                          {availability.name}
                        </h4>
                        <div className="flex flex-wrap gap-2">
                          {slots.map((slot) => (
                            <TokenSlotButton
                              key={slot.id}
                              slot={slot}
                              availability={availability}
                              selectedSlotId={selectedSlot?.id}
                              onClick={() =>
                                setSelectedSlot({ ...slot, availability })
                              }
                            />
                          ))}
                        </div>
                      </div>
                    ),
                  )
                ) : (
                  <div>{t("no_slots_available")}</div>
                )}
              </div>
            </div>
          </div>
        </div>
      </div>
      <div className="bg-secondary-200 h-20">
        {selectedSlot?.id && (
          <div className="container mx-auto flex flex-row justify-end mt-6">
            {appointment?.status !== "in_consultation" && (
              <Button
                variant="primary_gradient"
                disabled={
                  isReservingAppointment ||
                  isReschedulingAppointment ||
                  (!!appointmentId && !appointment)
                }
                onClick={() => void handleContinue()}
              >
                {(isReservingAppointment || isReschedulingAppointment) && (
                  <Loader2 className="size-4 animate-spin self-center mr-2" />
                )}
                {appointmentId ? t("reschedule_appointment") : t("continue")}
              </Button>
            )}
          </div>
        )}
      </div>
    </div>
  );
}
